import React, { useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { Button } from 'gin-and-tonic/common/button/Button'
import {
  type CollectionsFormData,
  type CollectionsFormModeProps,
} from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { collectionsFormTestIds } from 'domains/collections/components/collections-form/utils/collectionsForm.utils'
import { useCollectionsForm } from 'domains/collections/components/collections-form/utils/collectionsForm.hooks'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { CollectionsRules } from 'domains/collections/components/collections-form/components/rules/CollectionsRules'
import { CollectionsHeader } from 'domains/collections/components/collections-form/components/header/CollectionsHeader'
import { CollectionsStorefrontBanner } from 'domains/collections/components/collections-form/components/storefront-banner/CollectionsStorefrontBanner'
import { CollectionsTile } from 'domains/collections/components/collections-form/components/tile/CollectionsTile'
import {
  CollectionsContent,
  CollectionsContentType,
} from 'domains/collections/components/collections-form/components/content/CollectionsContent'
import { type CollectionTypeEnum } from 'domains/collections/api'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { CollectionsChildCollections } from './components/child-collections/CollectionsChildCollections'
import { CollectionsDetails } from './components/details/CollectionsDetails'

const wrapperStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  justifyItems: 'flex-start',
  width: '100%',
  alignItems: 'center',
  backgroundColor: 'white',
})

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  justifyItems: 'flex-start',
  maxWidth: 960,
  width: '80%',
})

export interface CollectionsListProps extends CollectionsFormModeProps {
  goToCollectionList: () => void
  retailerId?: string
  formData: CollectionsFormData
  collectionType: CollectionTypeEnum
}

export const CollectionsFormContent: React.FunctionComponent<CollectionsListProps> = ({
  goToCollectionList,
  formData,
  formModeDetails,
  collectionType,
  ...rest
}) => {
  const [contentType, setContentType] = useState<CollectionsContentType>(
    formData?.children?.length
      ? CollectionsContentType.childCollections
      : CollectionsContentType.rules
  )

  const {
    onSubmit,
    messages,
    errors,
    register,
    getValues,
    setValue,
    control,
    formSubmitSuccess,
    disableFormFields,
    formErrorMessageId,
    watch,
  } = useCollectionsForm({
    ...rest,
    formModeDetails,
    initialValues: formData,
    contentType,
  })

  const errorMessages = useDomainMessages({
    formError: formErrorMessageId,
    validationError: 'collectionsDomain.collectionsForm.validation.treeStructure.error',
  })

  useEffect(() => {
    if (formSubmitSuccess) {
      goToCollectionList()
    }
  }, [formSubmitSuccess, goToCollectionList])

  return (
    <div css={wrapperStyles}>
      <form css={formStyles} onSubmit={onSubmit} data-testid={collectionsFormTestIds.form}>
        <CollectionsDetails
          control={control}
          retailerId={rest.retailerId}
          errors={errors}
          register={register}
          disableFormFields={disableFormFields}
          getValues={getValues}
          setValue={setValue}
          collectionId={formModeDetails.collectionId}
          collectionType={collectionType}
        />

        <CollectionsContent onSelect={setContentType} value={contentType} />

        {contentType === CollectionsContentType.rules ? (
          <CollectionsRules
            control={control}
            register={register}
            watch={watch}
            retailerId={rest.retailerId}
            errors={errors}
          />
        ) : (
          <CollectionsChildCollections
            register={register}
            setValue={setValue}
            getValues={getValues}
            errors={errors}
            retailerId={rest.retailerId}
            collectionId={formModeDetails.collectionId}
            collectionType={collectionType}
          />
        )}

        <CollectionsTile
          showFormFragment={watch('showTileFragment')}
          getValues={getValues}
          errors={errors}
          register={register}
          control={control}
        />

        <CollectionsHeader
          showFormFragment={watch('showHeaderFragment')}
          errors={errors}
          register={register}
          setValue={setValue}
          getValues={getValues}
          control={control}
        />

        <CollectionsStorefrontBanner
          showFormFragment={watch('showStorefrontBannerFragment')}
          getValues={getValues}
          errors={errors}
          register={register}
          control={control}
        />

        <ErrorMessage collapse={false} data-testid={collectionsFormTestIds.formError}>
          {errorMessages.formError}
        </ErrorMessage>

        {errors.validation?.treeStructure && (
          <ErrorMessage collapse={false}>{errorMessages.validationError}</ErrorMessage>
        )}

        <Button
          disabled={disableFormFields}
          type="submit"
          data-testid={collectionsFormTestIds.submitButton}
        >
          {messages.submitButton}
        </Button>
      </form>
    </div>
  )
}
